import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import apiClient, { ApiError, RateLimitError } from '../utils/apiClient';

// Auth action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  SET_LOADING: 'SET_LOADING',
  UPDATE_SUBSCRIPTION: 'UPDATE_SUBSCRIPTION',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Initial auth state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  subscription: {
    plan: null,
    status: null,
    expiresAt: null,
    features: []
  }
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        subscription: action.payload.subscription || state.subscription
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        isLoading: false
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case AUTH_ACTIONS.UPDATE_SUBSCRIPTION:
      return {
        ...state,
        subscription: {
          ...state.subscription,
          ...action.payload
        }
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    default:
      return state;
  }
};

// Create auth context
const AuthContext = createContext(undefined);

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Using the centralized API client

  // Helper function to get stored auth data
  const getStoredAuthData = useCallback(() => {
    try {
      const token = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      const subscriptionData = localStorage.getItem('subscription_data');
      
      return {
        token,
        user: userData ? JSON.parse(userData) : null,
        subscription: subscriptionData ? JSON.parse(subscriptionData) : null
      };
    } catch (error) {
      console.error('Error parsing stored auth data:', error);
      return { token: null, user: null, subscription: null };
    }
  }, []);

  // Helper function to store auth data
  const storeAuthData = useCallback((token, user, subscription) => {
    try {
      if (token) localStorage.setItem('auth_token', token);
      if (user) localStorage.setItem('user_data', JSON.stringify(user));
      if (subscription) localStorage.setItem('subscription_data', JSON.stringify(subscription));
    } catch (error) {
      console.error('Error storing auth data:', error);
    }
  }, []);

  // Helper function to clear stored auth data
  const clearStoredAuthData = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('subscription_data');
  }, []);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      const { token, user, subscription } = getStoredAuthData();
      
      if (token && user) {
        try {
          // Verify token with backend using API client
          const data = await apiClient.get('/auth/verify');
          
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: {
              user: data.user || user,
              subscription: data.subscription || subscription
            }
          });
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) {
            // Token is invalid
            clearStoredAuthData();
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          } else {
            console.error('Auth verification failed, clearing stored data:', error);
            // Clear invalid stored data - don't trust it
            clearStoredAuthData();
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    checkAuthStatus();
  }, [getStoredAuthData, clearStoredAuthData]);

  // Listen for token expiration events from API client
  useEffect(() => {
    const handleTokenExpiration = () => {
      console.log('Token expiration detected, logging out user...');
      clearStoredAuthData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };

    // Listen for the custom event dispatched by the API client
    window.addEventListener('auth:token-expired', handleTokenExpiration);

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpiration);
    };
  }, [clearStoredAuthData]);
  
  // Login function
  const login = useCallback(async ({ email, password }) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const data = await apiClient.post('/auth/login', { email, password });
      
      // Check if payment is required
      if (data.requires_payment) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return { 
          success: true, 
          requires_payment: true,
          checkout_url: data.checkout_url,
          session_id: data.session_id,
          message: data.message 
        };
      }
      
      const { token, user, subscription } = data;
      storeAuthData(token, user, subscription);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, subscription }
      });
      
      return { success: true };
    } catch (error) {
      let errorMessage = 'Login failed';
      
      if (error instanceof RateLimitError) {
        errorMessage = `Too many login attempts. Please wait ${error.retryAfter} seconds.`;
      } else if (error instanceof ApiError) {
        errorMessage = "I'm sorry, we're having trouble right now. If this persists, please contact support@weederboard.com";
      } else {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, error: errorMessage };
    }
  }, [storeAuthData]);

  // Register function
  const register = useCallback(async ({ email, password, firstName, lastName }) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });

    try {
      const data = await apiClient.post('/auth/register', { 
        email, 
        password, 
        firstName, 
        lastName, 
        plan: 'premium'  // All users are premium
      });
      
      // New flow: registration returns checkout URL instead of user data
      // User account will be created after successful payment via webhook
      if (data.checkout_url) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return { 
          success: true, 
          checkout_url: data.checkout_url,
          session_id: data.session_id,
          message: data.message 
        };
      } else {
        // Fallback for any edge cases
        const { token, user, subscription } = data;
        if (token && user) {
          storeAuthData(token, user, subscription);
          dispatch({
            type: AUTH_ACTIONS.REGISTER_SUCCESS,
            payload: { user, subscription }
          });
          return { success: true, user, subscription };
        }
        throw new Error('Invalid registration response');
      }
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error instanceof RateLimitError) {
        errorMessage = `Too many registration attempts. Please wait ${error.retryAfter} seconds.`;
      } else if (error instanceof ApiError) {
        errorMessage = "I'm sorry, we're having trouble right now. If this persists, please contact support@weederboard.com";
      } else {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: { error: errorMessage }
      });
      return { success: false, error: errorMessage };
    }
  }, [storeAuthData]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const { token } = getStoredAuthData();
      
      if (token) {
        // Notify backend of logout (optional)
        await apiClient.post('/auth/logout');
      }
    } catch (error) {
      console.error('Logout request failed:', error);
      // Continue with logout even if backend call fails
    } finally {
      clearStoredAuthData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  }, [getStoredAuthData, clearStoredAuthData]);

  // Update subscription function
  const updateSubscription = useCallback(async (subscriptionData) => {
    try {
      const data = await apiClient.put('/subscription/update', subscriptionData);
      const newSubscription = data.subscription;
      
      // Update local storage
      localStorage.setItem('subscription_data', JSON.stringify(newSubscription));
      
      dispatch({
        type: AUTH_ACTIONS.UPDATE_SUBSCRIPTION,
        payload: newSubscription
      });
      
      return { success: true, subscription: newSubscription };
    } catch (error) {
      let errorMessage = 'Failed to update subscription';
      
      if (error instanceof RateLimitError) {
        errorMessage = `Too many requests. Please wait ${error.retryAfter} seconds.`;
      } else if (error instanceof ApiError) {
        errorMessage = "I'm sorry, we're having trouble right now. If this persists, please contact support@weederboard.com";
      } else {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      return { success: false, error: errorMessage };
    }
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // Check if user has specific feature access
  const hasFeatureAccess = useCallback((feature) => {
    if (!state.isAuthenticated || !state.subscription) {
      return false;
    }

    const { plan, status } = state.subscription;
    
    // Define feature access by plan
    const featureAccess = {
      free: ['basic_deals', 'community_access'],
      premium: [
        'basic_deals', 
        'community_access', 
        'advanced_filtering', 
        'price_history', 
        'custom_alerts', 
        'mobile_app',
        'unlimited_deals'
      ],
    };

    // For free plan, always allow free features
    if (plan === 'free') {
      return featureAccess.free.includes(feature);
    }

    // For premium plan, check if subscription is active
    if (plan === 'premium' && status === 'active') {
      return featureAccess.premium.includes(feature);
    }

    // If premium but not active, fall back to free features
    return featureAccess.free.includes(feature);
  }, [state.isAuthenticated, state.subscription]);

  // Check if user has active premium subscription
  const hasPremiumAccess = useCallback(() => {
    if (!state.isAuthenticated || !state.subscription) {
      return false;
    }
    
    const { plan, status } = state.subscription;
    return plan === 'premium' && status === 'active';
  }, [state.isAuthenticated, state.subscription]);

  // Get auth token for API requests
  const getAuthToken = useCallback(() => {
    const { token } = getStoredAuthData();
    return token;
  }, [getStoredAuthData]);

  // Context value
  const value = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    subscription: state.subscription,
    
    // Actions
    login,
    register,
    logout,
    updateSubscription,
    clearError,
    
    // Utilities
    hasFeatureAccess,
    hasPremiumAccess,
    getAuthToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Safety check to ensure context is an object
  if (!context || typeof context !== 'object') {
    console.error('AuthContext is not a valid object:', context);
    return {};
  }
  
  return context;
};

// HOC for components that require authentication
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();
    
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
            <p className="text-gray-600">Please sign in to access this feature.</p>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
};

export default AuthContext;
